import { robotData } from '../constants/robotData';

function normalizeKey(value) {
  return String(value || '').trim().toLowerCase();
}

function cleanText(value) {
  return String(value || '')
    .replace(/[\u2013\u2014\u2012\u2015]/g, ' ') // vervang Unicode-streepjes (en-dash, em-dash, etc.) door spatie
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const robotNameByKNummer = new Map();
const robotNameByProjectGemeente = new Map();
const robotNameByNumber = new Map();
const ambiguousRobotNumbers = new Set();

for (const item of robotData || []) {
  const canonicalName = cleanText(item?.naam || item?.name || '');
  if (!canonicalName) continue;

  const leadingNumber = canonicalName.match(/^(\d{2,6})\b/);
  if (leadingNumber) {
    const numberKey = leadingNumber[1];
    if (ambiguousRobotNumbers.has(numberKey)) {
      // Keep skipped when multiple canonical names share the same number.
    } else if (robotNameByNumber.has(numberKey) && robotNameByNumber.get(numberKey) !== canonicalName) {
      robotNameByNumber.delete(numberKey);
      ambiguousRobotNumbers.add(numberKey);
    } else {
      robotNameByNumber.set(numberKey, canonicalName);
    }
  }

  const kNummer = normalizeKey(item?.kNummer || item?.kNumber);
  if (kNummer && kNummer !== 'onbekend!') {
    robotNameByKNummer.set(kNummer, canonicalName);
  }

  const project = normalizeKey(item?.projectNummer || item?.projectCode);
  const gemeente = normalizeKey(item?.gemeente);
  if (project && gemeente) {
    robotNameByProjectGemeente.set(`${project}|${gemeente}`, canonicalName);
  }
}

export function normalizeRobotDisplayName(value) {
  const cleaned = cleanText(value);
  if (!cleaned) return '';

  const trailingNumber = cleaned.match(/^(.+?)\s+(\d{2,6})$/);
  if (trailingNumber) {
    const baseName = trailingNumber[1].trim();
    // Keep names like "361 Geusselt Noord Veld 12" unchanged.
    if (/^\d{2,6}\b/.test(baseName)) {
      return `${baseName} ${trailingNumber[2]}`;
    }
    return `${trailingNumber[2]} ${baseName}`;
  }

  const leadingNumber = cleaned.match(/^(\d{2,6})\s+(.+)$/);
  if (leadingNumber) {
    return `${leadingNumber[1]} ${leadingNumber[2].trim()}`;
  }

  return cleaned;
}

function pickRobotSource(data = {}) {
  const selectedRobot = data?.selectedRobot || {};
  const selectedRobotRobot = selectedRobot?.robot || {};

  const rawName = [
    data?.name,
    data?.naam,
    data?.robotName,
    data?.robotNaam,
    data?.robot,
    data?.value,
    selectedRobot?.name,
    selectedRobot?.naam,
    selectedRobot?.value,
    selectedRobotRobot?.name,
    selectedRobotRobot?.naam,
    selectedRobotRobot?.value,
    data?.label,
    selectedRobot?.label,
  ].map(cleanText).find(Boolean) || '';

  const rawNameLeadingNumberMatch = rawName.match(/^(\d{2,6})\b/);
  const explicitRobotNumber = cleanText(data?.robotNummer || data?.robotNumber || '');

  return {
    rawName: [
      rawName,
    ].find(Boolean) || '',
    robotNumber: explicitRobotNumber || (rawNameLeadingNumberMatch ? rawNameLeadingNumberMatch[1] : ''),
    kNummer: cleanText(
      data?.kNummer
      || data?.kNumber
      || selectedRobot?.kNummer
      || selectedRobot?.kNumber
      || selectedRobotRobot?.kNummer
      || selectedRobotRobot?.kNumber
      || ''
    ),
    project: normalizeKey(
      data?.projectNummer
      || data?.projectCode
      || selectedRobot?.projectNummer
      || selectedRobot?.projectCode
      || selectedRobotRobot?.projectNummer
      || selectedRobotRobot?.projectCode
      || ''
    ),
    gemeente: normalizeKey(
      data?.gemeente
      || selectedRobot?.gemeente
      || selectedRobotRobot?.gemeente
      || ''
    ),
  };
}

export function getCanonicalRobotName(data = {}, fallback = 'Onbekend', options = {}) {
  const { preferLookup = false } = options || {};
  const { rawName, robotNumber, kNummer, project, gemeente } = pickRobotSource(data);

  // App-entered names are source of truth. Lookup tables are only fallback.
  const canonicalFromLookup =
    (kNummer && robotNameByKNummer.get(normalizeKey(kNummer)))
    || ((project && gemeente) ? robotNameByProjectGemeente.get(`${project}|${gemeente}`) : '')
    || (robotNumber ? robotNameByNumber.get(robotNumber) : '')
    || '';

  const normalizedRawName = normalizeRobotDisplayName(rawName);
  const resolved = preferLookup
    ? (canonicalFromLookup || normalizedRawName)
    : (normalizedRawName || canonicalFromLookup);
  return resolved || fallback;
}

export function syncRobotAliases(data = {}, { allowCanonicalNameFallback = true, preferCanonicalName = false } = {}) {
  const normalized = { ...data };
  const explicitName = cleanText(normalized.name || normalized.naam || '');
  const canonicalName = allowCanonicalNameFallback || preferCanonicalName
    ? cleanText(getCanonicalRobotName(normalized, ''))
    : '';
  const resolvedName = preferCanonicalName ? (canonicalName || explicitName) : (explicitName || canonicalName);

  if (resolvedName) {
    normalized.name = resolvedName;
    normalized.naam = resolvedName;
  }

  const kNummer = cleanText(normalized.kNummer || normalized.kNumber || '');
  if (kNummer) {
    normalized.kNummer = kNummer;
    normalized.kNumber = kNummer;
  }

  const projectNummer = cleanText(normalized.projectNummer || normalized.projectCode || '');
  if (projectNummer) {
    normalized.projectNummer = projectNummer;
    normalized.projectCode = projectNummer;
  }

  return normalized;
}

export function getRobotOptionLabel(robot = {}) {
  const displayName = getCanonicalRobotName(robot);
  const gemeente = cleanText(robot?.gemeente);
  const project = cleanText(robot?.projectNummer || robot?.projectCode);
  const kNummer = cleanText(robot?.kNummer || robot?.kNumber);
  const meta = [gemeente, project, kNummer].filter(Boolean).join(' • ');
  return meta ? `${displayName} - ${meta}` : displayName;
}

export function getRobotSearchString(robot = {}) {
  const displayName = getCanonicalRobotName(robot, '');
  const selectedRobot = robot?.selectedRobot || {};
  const nestedRobot = selectedRobot?.robot || {};
  return [
    displayName,
    robot?.name,
    robot?.naam,
    robot?.robot,
    robot?.robotName,
    robot?.robotNaam,
    robot?.gemeente,
    robot?.projectNummer,
    robot?.projectCode,
    robot?.kNummer,
    robot?.kNumber,
    robot?.id,
    selectedRobot?.label,
    selectedRobot?.value,
    nestedRobot?.name,
    nestedRobot?.naam,
  ].map(cleanText).filter(Boolean).join(' ').toLowerCase();
}
